/**
 * Clear, user-facing reasons a join / rejoin failed. Accepts both an
 * AxiosError and the bare `response` object that `useHttpMutation` rethrows
 * (which is `undefined` when the request never reached the server).
 */

export type RoomErrorKind =
  | 'network'
  | 'unavailable'
  | 'full'
  | 'expired'
  | 'not-member'
  | 'unknown';

interface ResponseLike {
  status?: number;
  data?: unknown;
}

type ErrorLike = ResponseLike & {
  response?: ResponseLike;
  isAxiosError?: boolean;
};

const responseOf = (err: unknown): ResponseLike | null => {
  if (!err || typeof err !== 'object') return null;
  const e = err as ErrorLike;
  if (e.response) return e.response;
  if (typeof e.status === 'number') return e;
  return null;
};

/** No response at all: offline, DNS, CORS, timeout. */
const isNetworkFailure = (err: unknown) => {
  // useHttpMutation rethrows `err.response`, which is undefined offline
  if (err === undefined || err === null) return true;
  const e = err as ErrorLike;
  return !!e.isAxiosError && !e.response;
};

export const roomErrorKind = (err: unknown): RoomErrorKind => {
  if (isNetworkFailure(err)) return 'network';
  const res = responseOf(err);
  switch (res?.status) {
    case 404:
    case 410:
      return 'unavailable';
    case 409:
      return 'full';
    case 401:
      return 'expired';
    case 403:
      return 'not-member';
    default:
      return 'unknown';
  }
};

export const ROOM_ERROR_COPY: Record<
  Exclude<RoomErrorKind, 'unknown'>,
  string
> = {
  network:
    "Can't reach the Bhabi table right now. Check your connection and try again.",
  unavailable:
    'This table is no longer available. The game may have finished or the table was closed.',
  full: 'This table is full or the game has moved on without you.',
  expired: 'Your session has expired. Sign in again, then rejoin the table.',
  'not-member': "You're no longer seated at this table.",
};

const serverMessage = (res: ResponseLike | null): string | null => {
  const data = res?.data as { message?: unknown; error?: unknown } | string;
  if (typeof data === 'string') return data.trim() || null;
  const msg = data?.message ?? data?.error;
  return typeof msg === 'string' && msg.trim() ? msg.trim() : null;
};

/** Server copy wins (it is already user-facing); otherwise a status-based reason. */
export const roomErrorMessage = (err: unknown, fallback: string): string => {
  const kind = roomErrorKind(err);
  if (kind === 'network') return ROOM_ERROR_COPY.network;
  const fromServer = serverMessage(responseOf(err));
  if (fromServer) return fromServer;
  return kind === 'unknown' ? fallback : ROOM_ERROR_COPY[kind];
};
